<template>
  <v-container :fluid="true">
    <v-card :flat="true">
      <!-- タイトル -->
      <v-card-title class="mt-3 pb-5 text-h5 font-weight-bold">
        仮会員登録用QRコード
      </v-card-title>

      <!-- ============ 本部権限(HQ)のみ：店舗セレクト ============ -->
      <v-card :flat="true" v-if="isHQ">
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12" sm="6" md="6">
              <v-autocomplete
                v-model="selectedShopCode"
                :items="selectableShops"
                item-title="label"
                item-value="shop_code"
                label="店舗名"
                placeholder="店舗名を入力して絞り込み"
                :no-data-text="'該当する店舗がありません'"
                variant="outlined"
                density="compact"
                clearable
                @update:model-value="onSelectShop"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- ============ /本部権限：店舗セレクト ============ -->

      <!-- 注意文：機能無効店舗（店舗情報取得完了後にのみ表示しちらつきを防止） -->
      <v-alert
        v-if="selectedShopCode && tmpMemberEnabledChecked && !tmpMemberEnabled"
        type="warning"
        density="compact"
        class="mx-4 my-4"
      >
        この店舗は「仮会員登録」機能が無効です。設定を有効化してください。
      </v-alert>

      <!-- 店舗権限：読み込み中の文言（初回） -->
      <!-- isLoading は runInitialLoad 開始後に true になるため、初回描画時のちらつきを防止 -->
      <v-alert
        v-if="isStore && isLoading"
        type="info"
        density="compact"
        class="mx-4 my-4"
      >
        店舗情報を取得しています…
      </v-alert>

      <!-- ============ QR群 ============ -->
      <v-card-text v-if="registerUrl && tmpMemberEnabled">
        <!-- QRコード印刷 -->
        <div class="text-subtitle-1 font-weight-bold">QRコード印刷</div>
        <div>
          <v-btn
            class="mx-1 my-2"
            variant="outlined"
            @click="printQr()"
            :disabled="!tmpMemberEnabled"
          >
            印刷
          </v-btn>
        </div>

        <!-- QRコードダウンロード -->
        <v-divider class="my-6"></v-divider>
        <div class="text-subtitle-1 font-weight-bold">QRコードダウンロード</div>
        <span>サイズを選んでダウンロードできます。</span>
        <v-row align="end">
          <!-- small -->
          <v-col cols="12" md="4" class="text-center">
            <div class="mb-2 text-grey-darken-1">
              200 x 200 ピクセル<br />
              印刷サイズの目安: 10mm ～ 14mm
            </div>
            <canvas ref="qrDispSmRef"></canvas>
            <div class="mt-3">
              <v-btn
                class="mx-1"
                @click="downloadPng('sm')"
                :disabled="!tmpMemberEnabled"
              >
                ダウンロード
              </v-btn>
            </div>
          </v-col>

          <!-- medium -->
          <v-col cols="12" md="4" class="text-center">
            <div class="mb-2 text-grey-darken-1">
              400 x 400 ピクセル<br />
              印刷サイズの目安: 10mm ～ 29mm
            </div>
            <canvas ref="qrDispMdRef"></canvas>
            <div class="mt-3">
              <v-btn
                class="mx-1"
                @click="downloadPng('md')"
                :disabled="!tmpMemberEnabled"
              >
                ダウンロード
              </v-btn>
            </div>
          </v-col>

          <!-- large -->
          <v-col cols="12" md="4" class="text-center">
            <div class="mb-2 text-grey-darken-1">
              600 x 600 ピクセル<br />
              印刷サイズの目安: 10mm ～ 43mm
            </div>
            <canvas class="disp-print" ref="qrDispLgRef"></canvas>
            <div class="mt-3">
              <v-btn
                class="mx-1"
                @click="downloadPng('lg')"
                :disabled="!tmpMemberEnabled"
              >
                ダウンロード
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!-- URL 表示＆コピー -->
        <v-divider class="my-6"></v-divider>
        <div class="text-subtitle-1 font-weight-bold">仮会員登録サイトURL</div>
        <div>クリックで仮会員登録サイトが新規タブで開きます。</div>
        <div class="mt-2">
          <a :href="registerUrl" target="_blank" rel="noopener">{{
            registerUrl
          }}</a>
        </div>
      </v-card-text>

      <!-- 本部で未選択時のガイダンス -->
      <v-card-text
        v-if="isHQ && !selectedShopCode"
        class="text-grey-darken-1"
      >
        店舗を選択するとQRコードが表示されます。
      </v-card-text>
    </v-card>

    <!-- エラーダイアログ -->
    <AlertDialog
      v-model="errDialog"
      title="エラー"
      :message="errMessage"
      type="error"
    />
  </v-container>
</template>

<script setup lang="ts">
import { onMounted } from "vue";

// ------ Composables ------
import { useTmpMemberQrCodeUiState } from "./composables/useTmpMemberQrCodeUiState";
import { useTmpMemberQrCodeInit } from "./composables/useTmpMemberQrCodeInit";
import { useTmpMemberQrCodeUrl } from "./composables/useTmpMemberQrCodeUrl";
import { useTmpMemberQrCodeQr } from "./composables/useTmpMemberQrCodeQr";

// ------ Local Components ------
import AlertDialog from "@/components/AlertDialog.vue";

// ============================
// 画面UI状態
// ============================
const {
  errDialog,
  errMessage,
  isStore,
  isHQ,
  showError,
} = useTmpMemberQrCodeUiState();

// ============================
// 初期化・店舗選択
// resolveAndRenderUrl は function declaration でホイスティングされるため
// Init の初期化時点で参照可能（実際の呼び出しは onMounted / イベント時）
// ============================
const {
  selectedShopCode,
  currentInternalId,
  tmpMemberEnabled,
  tmpMemberEnabledChecked,
  isLoading,
  selectableShops,
  runInitialLoad,
  onSelectShop,
} = useTmpMemberQrCodeInit({
  isStore,
  isHQ,
  showError,
  resolveAndRenderUrl,
  clearSelection,
});

// ============================
// URL生成
// ============================
const {
  registerUrl,
  generateAndSetUrl,
  clearUrl,
} = useTmpMemberQrCodeUrl({
  currentInternalId,
});

// ============================
// QR描画・出力
// ============================
const {
  qrDispSmRef,
  qrDispMdRef,
  qrDispLgRef,
  renderAllQrs,
  downloadPng,
  printQr,
} = useTmpMemberQrCodeQr({
  registerUrl,
  selectedShopCode,
  showError,
});

// 店舗セレクタクリア時のURL・QRリセット
// Init composable が onSelectShop 内でこの関数を呼び出す
// function declaration によりホイスティングされるため、上記 Init の初期化前に参照可能
function clearSelection() {
  clearUrl();
}

// URL生成→QR描画の連携
// Init composable が runInitialLoad / onSelectShop 内でこの関数を呼び出す
// function declaration によりホイスティングされるため、上記 Init の初期化前に参照可能
async function resolveAndRenderUrl() {
  try {
    generateAndSetUrl();
    await renderAllQrs();
  } catch (e: unknown) {
    showError("URL生成に失敗しました。");
  }
}

// ============================
// ライフサイクル
// ============================
onMounted(async () => {
  await runInitialLoad();
});
</script>

<style scoped>
@page {
  margin: 0;
}
</style>
